/**
 * Where `EntityLogo` finds a flag or a brand logo (decision D-8). Pure resolution, no rendering:
 * a flag code or a brand slug in, an asset URL or `undefined` out, so the tile can fall back to
 * its initial without the component knowing where assets live.
 *
 * Flag codes are explicit about what they name, because the bare codes in the data collide: `CA`
 * is Canada and California, `PA` is Panama and Pennsylvania, `NA` is Namibia and North America.
 * A country is its ISO 3166-1 alpha-2 code (`gb`, `br`); a US state is `us-<postal code>`
 * (`us-ny`). Anything else resolves to nothing and the caller shows the letters tile.
 *
 * Country flags come from the `flag-icons` package (MIT): the square `1x1` set, picked up as
 * hashed build assets by `import.meta.glob` — `?no-inline` keeps Vite from turning the small ones
 * into data URIs in the entry bundle, so each flag is one request when it is first shown. US
 * states are the pre-rasterised tiles in `public/flags/us/` (see `public/flags/README.md` for
 * sources and licences). Brand logos would live in `public/logos/<slug>.svg`; none are licensed
 * yet, so `BRAND_LOGOS` is empty and every brand gets its initial.
 */

const COUNTRY_FLAG_FILES = import.meta.glob<string>('/node_modules/flag-icons/flags/1x1/*.svg', {
  eager: true,
  query: '?no-inline',
  import: 'default',
});

/** @desc ISO code → asset URL, for every flag the package ships (`gb`, `gb-eng`, `eu`, …). */
const COUNTRY_FLAGS: ReadonlyMap<string, string> = new Map(
  Object.entries(COUNTRY_FLAG_FILES).map(([path, url]) => [
    path.slice(path.lastIndexOf('/') + 1).replace(/\.svg$/, ''),
    url,
  ]),
);

/** @desc US states with a tile in `public/flags/us/` — keep in step with that folder's README. */
export const US_STATE_FLAGS = ['ca', 'fl', 'mi', 'nj', 'ny', 'pa'] as const;

const US_STATES: ReadonlySet<string> = new Set(US_STATE_FLAGS);

/** @desc Brand slugs with a logo in `public/logos/<slug>.svg`. None are licensed yet (D-8). */
export const BRAND_LOGOS: ReadonlySet<string> = new Set();

/** @desc `us-ny` → `/flags/us/ny.webp` in the public dir; relative to the app's base URL. */
const US_STATE_TILE = (code: string) => `${import.meta.env.BASE_URL}flags/us/${code}.webp`;

const normalise = (code: string | null | undefined) => (code ?? '').trim().toLowerCase();

/**
 * @desc The flag image for a code: an ISO 3166-1 alpha-2 country (`gb`), a `flag-icons`
 * subdivision (`gb-eng`) or supranational (`eu`) code, or a US state as `us-<postal>` (`us-ny`).
 * `undefined` when there is no such asset — the caller falls back to the letters tile.
 */
export const flagSrc = (flagCode: string | null | undefined): string | undefined => {
  const code = normalise(flagCode);
  if (!code) return undefined;
  if (code.startsWith('us-')) {
    const state = code.slice(3);
    return US_STATES.has(state) ? US_STATE_TILE(state) : undefined;
  }
  return COUNTRY_FLAGS.get(code);
};

/** @desc Whether a code is one `flagSrc` can draw. */
export const hasFlag = (flagCode: string | null | undefined): boolean =>
  flagSrc(flagCode) !== undefined;

/** @desc A brand or group logo by its slug, or `undefined` when none is shipped. */
export const logoSrc = (slug: string | null | undefined): string | undefined => {
  const key = normalise(slug);
  return key && BRAND_LOGOS.has(key) ? `${import.meta.env.BASE_URL}logos/${key}.svg` : undefined;
};

/**
 * @desc The code for a US state flag from its postal abbreviation: `NY` → `us-ny`. A convenience
 * for data that stores states by postal code.
 */
export const usStateFlagCode = (postal: string): string => `us-${normalise(postal)}`;
